import React, { useEffect, useState } from "react";
import Select from "react-select";
import axios from "axios";
import axiosInstance from "../api/axiosInstance";



const UserRole = () => {
  const [groups, setGroups] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [users, setUsers] = useState([]);
  const [userTypes, setUserTypes] = useState([]);
  const [roles, setRoles] = useState([]);

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserType, setSelectedUserType] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);

  const [roleAccesses, setRoleAccesses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isMobileRole, setIsMobileRole] = useState(true);

  /* ================= FETCH DROPDOWN DATA ================= */
  useEffect(() => {
    fetchGroups();
    fetchUserTypes();
  }, []);

  const fetchGroups = async () => {
    try {
      const res = await axiosInstance.get(`/GroupMaster`);
      setGroups(res.data.map((g) => ({ value: g.groupId, label: g.groupName })));
    } catch (err) {
      console.error(err);
    }
  };

  const fetchRestaurants = async (groupId) => {
    try {
      const res = await axiosInstance.get(`/RestaurantMaster/GetByGroup?groupId=${groupId}`);
      setRestaurants(res.data.map((r) => ({ value: r.restId, label: r.restaurantName })));
    } catch (err) {
      console.error(err);
    }
  };

  const fetchUsers = async (restId) => {
    try {
      const res = await axiosInstance.get(`/UserMaster/GetUserMasterByRestaurantId?restId=${restId}`);
      setUsers(res.data.map((u) => ({ value: u.user_Id, label: u.user_Name })));
    } catch (err) {
      console.error(err);
    }
  };

  const fetchUserTypes = async () => {
    try {
      const res = await axiosInstance.get(`/UserTypeMaster`);
      setUserTypes(res.data.map((u) => ({ value: u.userTypeId, label: u.userTypeName })));
    } catch (err) {
      console.error(err);
    }
  };

  const fetchRolesByUserType = async (userTypeId) => {
    try {
      const res = await axiosInstance.get(`/RoleMaster/GetByUserType?userTypeId=${userTypeId}`);
      setRoles(res.data.map((r) => ({
        value: r.roleId,
        label: r.roleName
      })));
    } catch (err) {
      console.error(err);
    }
  };

  /* ================= FETCH USER ROLE ================= */
  const fetchUserRole = async (groupId, restId, userId, roleId, userTypeId) => {
    try {
      const roleRes = await axiosInstance.get(`/RoleMaster/GetByUserType?userTypeId=${userTypeId}`);
      let modules = roleRes.data[0]?.roleAccesses.map((m) => ({
        ...m,
        read: false,
        write: false,
        delete: false
      })) || [];

      const userRoleRes = await axiosInstance.get(`/UserRole/GetUserRole?groupId=${groupId}&restId=${restId}&userId=${userId}`);
      const userRoleAccesses = userRoleRes.data.roleAccesses || [];

      const merged = modules.map((m) => {
        const assigned = userRoleAccesses.find((u) => u.moduleId === m.moduleId);
        return assigned ? { ...m, ...assigned } : m;
      });

      setRoleAccesses(merged);
      setIsMobileRole(userRoleRes.data.isMobileRole ?? true);
    } catch (err) {
      console.error("Fetch User Role error:", err);
    }
  };

  /* ================== HANDLE ROLE SELECTION ================== */
  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    if (selectedGroup && selectedRestaurant && selectedUser && selectedUserType) {
      fetchUserRole(
        selectedGroup.value,
        selectedRestaurant.value,
        selectedUser.value,
        role.value,
        selectedUserType.value
      );
    }
  };

  /* ================== PERMISSION DEPENDENCY LOGIC ================== */
  const togglePermission = (index, field) => {
    const updated = [...roleAccesses];
    if (field === "read") {
      updated[index].read = !updated[index].read;
      if (!updated[index].read) { updated[index].write = false; updated[index].delete = false; }
    }
    if (field === "write") {
      updated[index].write = !updated[index].write;
      if (updated[index].write) { updated[index].read = true; } else { updated[index].delete = false; }
    }
    if (field === "delete") {
      updated[index].delete = !updated[index].delete;
      if (updated[index].delete) { updated[index].read = true; updated[index].write = true; }
    }
    setRoleAccesses(updated);
  };

  /* ================== SAVE USER ROLE ================== */
  const handleSave = async () => {
    if (!selectedGroup || !selectedRestaurant || !selectedUser || !selectedUserType || !selectedRole) {
      alert("Please select all fields");
      return;
    }
    try {
      setLoading(true);
      const payload = {
        groupId: selectedGroup.value,
        restId: selectedRestaurant.value,
        userId: selectedUser.value,
        roleId: selectedRole.value,
        userTypeId: selectedUserType.value,
        isMobileRole: isMobileRole,
        loginUserId: 1,
        roleAccesses
      };
      await axiosInstance.post(`/UserRole/UpsertUserRole`, payload);
      alert("User Role Assigned Successfully!");
    } catch (err) {
      console.error(err);
      alert("Failed to assign role.");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-5xl mx-auto bg-white shadow-lg rounded-lg p-6">
      <h2 className="text-xl font-bold mb-6">User Role Assign</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="font-medium text-gray-700 mb-1 block">Select Group</label>
          <Select
            options={groups} placeholder="Select Group" value={selectedGroup}
            onChange={(val) => { setSelectedGroup(val); setSelectedRestaurant(null); setSelectedUser(null); fetchRestaurants(val.value); }}
          />
        </div>

        <div>
          <label className="font-medium text-gray-700 mb-1 block">Select Restaurant</label>
          <Select
            options={restaurants} placeholder="Select Restaurant" value={selectedRestaurant}
            onChange={(val) => { setSelectedRestaurant(val); setSelectedUser(null); fetchUsers(val.value); }}
          />
        </div>

        <div>
          <label className="font-medium text-gray-700 mb-1 block">Select User</label>
          <Select options={users} placeholder="Select User" value={selectedUser} onChange={setSelectedUser} />
        </div>

        <div>
          <label className="font-medium text-gray-700 mb-1 block">Select User Type</label>
          <Select options={userTypes} placeholder="Select User Type" value={selectedUserType}
            onChange={(val) => { setSelectedUserType(val); setSelectedRole(null); setRoleAccesses([]); fetchRolesByUserType(val.value); }}
          />
        </div>

        <div>
          <label className="font-medium text-gray-700 mb-1 block">Select Role</label>
          <Select options={roles} placeholder="Select Role" value={selectedRole} onChange={handleRoleSelect} />
        </div>
      </div>

      <div className="mt-4 flex items-center">
        <input
          type="checkbox"
          checked={isMobileRole}
          onChange={() => setIsMobileRole(!isMobileRole)}
          className="mr-2"
        />
        <label className="text-gray-700 font-medium">Is Mobile Role</label>
      </div>

      {roleAccesses.length > 0 && (
        <div className="mt-6 overflow-x-auto">
          <h3 className="text-lg font-semibold mb-2">Role Permissions</h3>
          <table className="w-full text-sm border rounded-lg">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-2 text-left">Module</th>
                <th className="p-2 text-center">Read</th>
                <th className="p-2 text-center">Write</th>
                <th className="p-2 text-center">Delete</th>
              </tr>
            </thead>
            <tbody>
              {roleAccesses.map((mod, idx) => (
                <tr key={mod.moduleId} className="hover:bg-gray-50">
                  <td className="p-2 font-medium">{mod.moduleName}</td>
                  <td className="text-center"><input type="checkbox" checked={mod.read} onChange={() => togglePermission(idx, "read")} /></td>
                  <td className="text-center"><input type="checkbox" checked={mod.write} onChange={() => togglePermission(idx, "write")} /></td>
                  <td className="text-center"><input type="checkbox" checked={mod.delete} onChange={() => togglePermission(idx, "delete")} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex justify-end mt-6">
        <button onClick={handleSave} disabled={loading} className="bg-blue-600 text-white px-6 py-2 rounded-lg">
          {loading ? "Saving..." : "Assign Role"}
        </button>
      </div>
    </div>
  );
};

export default UserRole;